import React, { useState } from "react";
import Client from "../../models/client";
import { useForm } from "@mantine/form";
import { useSWRConfig } from "swr";
import { FETCH_KEYS, updateClientConf } from "../api";
import { notifications } from "@mantine/notifications";
import { Box, Button, Checkbox, Group, Text, TextInput, ThemeIcon, Title } from "@mantine/core";
import { WrenchIcon } from "@phosphor-icons/react";
import TagCodeBlock from "../components/TagCodeBlock";

interface Props {
  client: Client
}

const ComingConfContainer: React.FC<Props> = ({ client }) => {
  const serviceUse = String(client.invy_service_use)
  const showComing = serviceUse === '1' || serviceUse === '2'

  const urlName = client.url_name
  const pageCreationEnabled = String(client.page_creation) !== '0'

  const comingForm = useForm({
    initialValues: {
      comingName: client.coming_name,
      comingAddress: client.coming_address,
      comingMail: client.coming_mail,
      comingOther: client.coming_other,
      comingTel: client.coming_tel,
      shopId: client.shop_id,
      shopPrefecture: client.shop_prefecture,
      storeStaff: client.store_staff,
      storeOther: client.store_other,
      useGuestCouponRedirect: client.use_guest_coupon_redirect,
      comingPasswordRequired: client.coming_password_required,
      comingDisplayFromName: client.coming_display_from_name,
      comingDisplayCode: client.coming_display_code,
      comingDisplayFromShopName: client.coming_display_from_shop_name,
      password: client.password ?? '',
      privacypolicy: client.privacypolicy ?? '',
    },
    validate: {
      privacypolicy: (value: string) => {
        if (!pageCreationEnabled) return null
        if (!value) return '必須です'
        const regexp = RegExp('(https?://)?([\\da-z.-]+)\\.([a-z.]{2,6})[/\\w .-]*/?')
        if (!regexp.test(value)) return 'URLが正しくありません'
        return null
      },
      password: (value: string) => {
        if (value && value.length > 255) return '255文字以下で入力してください'
        return null
      },
    },
  })

  const { mutate } = useSWRConfig()
  const [submitting, setSubmitting] = useState(false)

  const buildPayload = (values: typeof comingForm.values) => {
    if (pageCreationEnabled) return values

    // pageCreation === '0' では画面非表示のため送信しない
    const { privacypolicy, ...rest } = values
    return rest
  }

  const handleSubmit = async (values: typeof comingForm.values) => {
    setSubmitting(true)

    try {
      await updateClientConf(client.id, buildPayload(values))
      mutate(FETCH_KEYS.fetchClient(client.id))
      notifications.show({ message: '保存しました', color: 'teal' })
    } catch (e) {
      console.error('来店計測設定の保存に失敗しました', e)
      notifications.show({ message: '保存に失敗しました', color: 'red' })
    } finally {
      setSubmitting(false)
    }
  }

  const comingTag = `<a href="#" onclick="redirect();">紹介来店クーポンを発行</a>
<script>
  function redirect() {
      var code = getCookieValue('c_invy_jp').split('%3C%3E')
      location.href = '${location.protocol}//${location.host}/${urlName}/coming_form' + "?code=" + code[0];
  }
  function getCookieValue(name) {
      var nameEQ = name + "=";
      var ca = document.cookie.split(';');
      for(var i=0;i < ca.length;i++) {
          var c = ca[i];
          while (c.charAt(0)==' ') c = c.substring(1,c.length);
          if (c.indexOf(nameEQ) == 0) return c.substring(nameEQ.length,c.length);
      }
      return null;
  }
</script>`

  return (
    <Box>
      <Group gap={12} mb={40}>
        <ThemeIcon variant="transparent" color="#5DA327">
          <WrenchIcon weight="fill" size={36} />
        </ThemeIcon>
        <Title size={24} fw={600} c="#000000CC">
          来店計測設定
        </Title>
      </Group>

      {showComing && (
        <form onSubmit={comingForm.onSubmit(handleSubmit)}>
          {pageCreationEnabled && (
            <>
              <Box mb={80}>
                <Title order={2} size={18} fw={600} mb={16}>
                  紹介ページ（guest）用来店計測タグ
                </Title>
                <Text fz={13} c="#00000099" mb={12}>
                  以下を友人（guest）ページHTMLの指定の場所に入れてください。
                  HTMLに反映しない場合は来店計測はできません。
                </Text>
                <TagCodeBlock code={comingTag} language="html" />
              </Box>

              <Box mb={40}>
                <Title order={2} size={18} fw={600} mb={16}>QRコード（全店舗共通）</Title>
                <Text fz={13} c="#00000099" mb={12}>
                  店舗別QRコードは「店舗管理」内の「QR」よりご確認ください。
                </Text>
                <Button
                  component="a"
                  href={`/admin/api/clients/${client.id}/shops/qr`}
                  variant="outline"
                  radius="xl"
                >
                  ダウンロード
                </Button>
              </Box>

              <Box mb={40}>
                <Title order={2} size={18} fw={600} mb={16}>プライバシーポリシーURL</Title>
                <TextInput
                  placeholder="https://..."
                  {...comingForm.getInputProps('privacypolicy')}
                />
              </Box>
            </>
          )}

          {/* 友人用情報入力項目はカンタンプランでも表示する（来店クーポン発行フォームの項目に使うため） */}
          <Box mb={40}>
            <Title order={2} size={18} fw={600} mb={16}>友人用情報入力項目</Title>
            <Text fz={13} c="#00000099" mb={12}>
              友人用のページで事前に入力してもらう情報になります。チェックした項目は必須項目として表示されます。<br />
              何もチェックを入れない場合は入力欄は表示されません。
            </Text>
            <Group gap={40}>
              <Checkbox
                style={{ cursor: 'pointer' }}
                label="お名前"
                {...comingForm.getInputProps('comingName', { type: 'checkbox' })}
              />
              <Checkbox
                style={{ cursor: 'pointer' }}
                label="住所"
                {...comingForm.getInputProps('comingAddress', { type: 'checkbox' })}
              />
              <Checkbox
                style={{ cursor: 'pointer' }}
                label="メールアドレス"
                {...comingForm.getInputProps('comingMail', { type: 'checkbox' })}
              />
              <Checkbox
                style={{ cursor: 'pointer' }}
                label="会員番号"
                {...comingForm.getInputProps('comingOther', { type: 'checkbox' })}
              />
              <Checkbox
                style={{ cursor: 'pointer' }}
                label="電話番号"
                {...comingForm.getInputProps('comingTel', { type: 'checkbox' })}
              />
            </Group>
          </Box>

          <Box mb={40}>
            <Title order={2} size={18} fw={600} mb={16}>店舗スタッフ認証パスワード設定</Title>
            <Text fz={13} c="#00000099" mb={12}>
              来店チェックの際に店舗担当者が入力するパスワードが設定できます。
            </Text>
            <TextInput {...comingForm.getInputProps('password')} />
          </Box>

          <Box mb={40}>
            <Title order={2} size={18} fw={600} mb={16}>店舗スタッフ入力項目</Title>
            <Text fz={13} c="#00000099" mb={12}>
              店舗スタッフがパスワード入力後に入力する項目になります。
            </Text>
            <Group gap={40} mb={8}>
              <Checkbox
                style={{ cursor: 'pointer' }}
                label="店舗名"
                {...comingForm.getInputProps('shopId', { type: 'checkbox' })}
              />
              <Checkbox
                style={{ cursor: 'pointer' }}
                label="都道府県別店舗選択"
                {...comingForm.getInputProps('shopPrefecture', { type: 'checkbox' })}
              />
            </Group>
            <Box mb={16}>
              <Text fz={12} c="#00000099">※店舗名を取得する場合は来店店舗マスタに登録が必要です。</Text>
              <Text fz={12} c="#00000099">※店舗数が30店舗を超える場合は必ず選択ください。</Text>
            </Box>
            <Group gap={40} mb={8}>
              <Checkbox
                style={{ cursor: 'pointer' }}
                label="担当スタッフ名"
                {...comingForm.getInputProps('storeStaff', { type: 'checkbox' })}
              />
              <Checkbox
                style={{ cursor: 'pointer' }}
                label="来店メモ"
                {...comingForm.getInputProps('storeOther', { type: 'checkbox' })}
              />
            </Group>
            <Checkbox
              style={{ cursor: 'pointer' }}
              label="ゲスト用の来店クーポン画面設定時にクーポン画面にリダイレクトする"
              {...comingForm.getInputProps('useGuestCouponRedirect', { type: 'checkbox' })}
            />
          </Box>

          <Box>
            <Title order={2} size={18} fw={600} mb={16}>新来店QR</Title>
            <Checkbox
              style={{ cursor: 'pointer' }}
              label="スタッフ確認用パスワード認証"
              {...comingForm.getInputProps('comingPasswordRequired', { type: 'checkbox' })}
            />
            <Text fz={13} fw={600} c="#000000CC" mt={16} mb={8}>紹介者情報</Text>
            <Group gap={40}>
              <Checkbox
                style={{ cursor: 'pointer' }}
                label="お名前"
                {...comingForm.getInputProps('comingDisplayFromName', { type: 'checkbox' })}
              />
              <Checkbox
                style={{ cursor: 'pointer' }}
                label="紹介者コード"
                {...comingForm.getInputProps('comingDisplayCode', { type: 'checkbox' })}
              />
              <Checkbox
                style={{ cursor: 'pointer' }}
                label="登録店舗"
                {...comingForm.getInputProps('comingDisplayFromShopName', { type: 'checkbox' })}
              />
            </Group>
          </Box>

          <Group justify="start" mt={40}>
            <Button
              type="submit"
              variant="filled"
              radius="xl"
              fz={13}
              fw={600}
              w={120}
              disabled={submitting}
            >
              {submitting ? '処理中' : '保存'}
            </Button>
          </Group>
        </form>
      )}
    </Box>
  )
}

export default ComingConfContainer
